import { Component, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BaseChartDirective } from 'ng2-charts';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { catchError, forkJoin, of } from 'rxjs';

import { SessionService } from '../../../auth/services/session/session.service';
import { LoadingService } from '../../../shared/services/loading/loading.service';
import { generateRandomColor } from '../../../shared/utils/random-color.util';
import { ConditionReportResponse } from '../../models/reports/condition-report.model';
import { InspectionReportResponse } from '../../models/reports/inspection-report.model';
import { ProductReportResponse } from '../../models/reports/product-report.model';
import { StationReportResponse } from '../../models/reports/station-report.model';
import { TrendPlagueResponse, TrendStationResponse } from '../../models/reports/trends-report.model';
import { ReportsService } from '../../services/reports/reports.service';

@Component({
  selector: 'app-customer-report',
  standalone: true,
  imports: [
    BaseChartDirective,
    DropdownModule,
    ButtonModule,
  ],
  templateUrl: './customer-report.component.html',
  styleUrl: './customer-report.component.scss'
})
export class CustomerReportComponent implements OnChanges, OnInit {
  @Input() clientId!: string;
  @Input() initialDate?: string;
  @Input() finalDate?: string;

  private _reportsService = inject(ReportsService);
  private _loadingService = inject(LoadingService);
  private _sessionService = inject(SessionService);

  public chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          usePointStyle: true,
          pointStyle: 'circle'
        }
      }
    }
  };

  public inspectionPlagueCategories: any | null = {};
  public inspectionPlagues: any | null = {};
  public inspectionLifeCycles: any | null = {};
  public inspectionEvidences: any | null = {};
  public inspectionDegrees: any | null = {};
  public conditionCleanings: any | null = {};
  public conditionCategories: any | null = {};
  public productApplications: any | null = {};
  public products: any | null = {};
  public productDoses: any | null = {};
  public stationsKeys: string[] = [];
  public stationActivities: any = {};
  public stationConditions: any = {};
  public stationPlagues: any = {};
  public trendPlague: any | null = null;
  public trendProduct: any | null = null;
  public trendStation: any | null = {};
  public trendStationKeys: string[] = [];
  public trendStationTitles: any = {};

  constructor(private elementRef: ElementRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] || changes['initialDate'] || changes['finalDate']) {
      this.loadData();
      this.loadTrends();
    }
  }

  ngOnInit(): void {
    this.loadData();
    this.loadTrends();
  }

  get isBlocked(): boolean {
    return this._sessionService.CompanyStatus !== 'company-paid';
  }

  loadData(): void {
    this._loadingService.show();

    forkJoin({
      inspectionGraphic: this._reportsService.getInspectionGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar inspectionGraphic:', error);
          return of({ data: { plague_categories: [], plagues: [], life_cycles: [], evidences: [], degree: [] }, code: 0, message: 'Error' } as InspectionReportResponse);
        })
      ),
      conditionGraphic: this._reportsService.getConditionGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar conditionGraphic:', error);
          return of({ data: { cleanings: [], categories: [] }, code: 0, message: 'Error' } as ConditionReportResponse);
        })
      ),
      productGraphic: this._reportsService.getProductGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar productGraphic:', error);
          return of({ data: { application: [], product: [], dose: [] }, code: 0, message: 'Error' } as ProductReportResponse);
        })
      ),
      stationGraphic: this._reportsService.getStationGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar stationGraphic:', error);
          return of({ data: [], code: 0, message: 'Error' } as StationReportResponse);
        })
      ),
      trendStationGraphic: this._reportsService.getTrendStationGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar trendStationGraphic:', error);
          return of({ data: {}, code: 0, message: 'Error' } as TrendStationResponse);
        })
      ),
    }).subscribe({
      next: (data: {
        inspectionGraphic: InspectionReportResponse,
        conditionGraphic: ConditionReportResponse,
        productGraphic: ProductReportResponse,
        stationGraphic: StationReportResponse,
        trendStationGraphic: TrendStationResponse,
      }) => {
        const inspectionPlagueCategoriesColors = (data.inspectionGraphic?.data?.plague_categories || []).map(() => generateRandomColor());
        const inspectionPlaguesColors = (data.inspectionGraphic?.data?.plagues || []).map(() => generateRandomColor());
        const inspectionLifeCyclesColors = (data.inspectionGraphic?.data?.life_cycles || []).map(() => generateRandomColor());
        const inspectionEvidencesColors = (data.inspectionGraphic?.data?.evidences || []).map(() => generateRandomColor());
        const inspectionDegreesColor = generateRandomColor()[0];

        const conditionCleaningsColors = (data.conditionGraphic?.data?.cleanings || []).map(() => generateRandomColor());
        const conditionCategoriesColors = (data.conditionGraphic?.data?.categories || []).map(() => generateRandomColor());

        const productApplicationsColors = (data.productGraphic?.data?.application || []).map(() => generateRandomColor());
        const productsColors = (data.productGraphic?.data?.product || []).map(() => generateRandomColor());
        const productDosesColors = (data.productGraphic?.data?.dose || []).map(() => generateRandomColor());

        this.stationsKeys = (data.stationGraphic?.data || []).map((station) => station.StationType);

        this.inspectionPlagueCategories = !data.inspectionGraphic?.data?.plague_categories || data.inspectionGraphic.data.plague_categories.length === 0
          ? null
          : {
            labels: data.inspectionGraphic.data.plague_categories.map((category) => `${category.type}: ${category.count} (${category.percentage}%)`),
            datasets: [{
              label: 'Proporción de Tipos de Plaga en Inspecciones',
              data: data.inspectionGraphic.data.plague_categories.map((category) => category.count),
              backgroundColor: inspectionPlagueCategoriesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: inspectionPlagueCategoriesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.inspectionPlagues = !data.inspectionGraphic?.data?.plagues || data.inspectionGraphic.data.plagues.length === 0
          ? null
          : {
            labels: data.inspectionGraphic.data.plagues.map((plague) => `${plague.type}: ${plague.count} (${plague.percentage}%)`),
            datasets: [{
              label: 'Proporción de Plagas en Inspecciones',
              data: data.inspectionGraphic.data.plagues.map((plague) => plague.count),
              backgroundColor: inspectionPlaguesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: inspectionPlaguesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.inspectionLifeCycles = !data.inspectionGraphic?.data?.life_cycles || data.inspectionGraphic.data.life_cycles.length === 0
          ? null
          : {
            labels: data.inspectionGraphic.data.life_cycles.map((lifeCycle) => `${lifeCycle.type}: ${lifeCycle.count} (${lifeCycle.percentage}%)`),
            datasets: [{
              label: 'Proporción de Ciclos de Vida en Inspecciones',
              data: data.inspectionGraphic.data.life_cycles.map((lifeCycle) => lifeCycle.count),
              backgroundColor: inspectionLifeCyclesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: inspectionLifeCyclesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.inspectionEvidences = !data.inspectionGraphic?.data?.evidences || data.inspectionGraphic.data.evidences.length === 0
          ? null
          : {
            labels: data.inspectionGraphic.data.evidences.map((evidence) => `${evidence.type}: ${evidence.count} (${evidence.percentage}%)`),
            datasets: [{
              label: 'Proporción de Evidencias en Inspecciones',
              data: data.inspectionGraphic.data.evidences.map((evidence) => evidence.count),
              backgroundColor: inspectionEvidencesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: inspectionEvidencesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.inspectionDegrees = !data.inspectionGraphic?.data?.degree || data.inspectionGraphic.data.degree.length === 0
          ? null
          : {
            labels: data.inspectionGraphic.data.degree.map((degree) => degree.date + ': ' + degree.highest_level),
            datasets: [{
              label: 'Gráfica de Tendencia de Plaga vs Grado de Infestación',
              data: data.inspectionGraphic.data.degree.map((degree) => degree.highest_level),
              fill: false,
              borderColor: inspectionDegreesColor,
              tension: 0.1
            }],
          };

        this.conditionCleanings = !data.conditionGraphic?.data?.cleanings || data.conditionGraphic.data.cleanings.length === 0
          ? null
          : {
            labels: data.conditionGraphic.data.cleanings.map((cleaning) => `${cleaning.cleaning}: ${cleaning.count} (${cleaning.percentage}%)`),
            datasets: [{
              label: 'Proporción de Evidencias en Inspecciones',
              data: data.conditionGraphic.data.cleanings.map((cleaning) => cleaning.count),
              backgroundColor: conditionCleaningsColors.map(([rgbaColor]) => rgbaColor),
              borderColor: conditionCleaningsColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.conditionCategories = !data.conditionGraphic?.data?.categories || data.conditionGraphic.data.categories.length === 0
          ? null
          : {
            labels: data.conditionGraphic.data.categories.map((category) => `${category.category}: ${category.count} (${category.percentage}%)`),
            datasets: [{
              label: 'Proporción de Tipo de Condiciones',
              data: data.conditionGraphic.data.categories.map((category) => category.count),
              backgroundColor: conditionCategoriesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: conditionCategoriesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.productApplications = !data.productGraphic?.data?.application || data.productGraphic.data.application.length === 0
          ? null
          : {
            labels: data.productGraphic.data.application.map((application) => `${application.method}: ${application.count} (${application.percentage}%)`),
            datasets: [{
              label: 'Porcentaje de Tipo de Tipo de Aplicación',
              data: data.productGraphic.data.application.map((application) => application.count),
              backgroundColor: productApplicationsColors.map(([rgbaColor]) => rgbaColor),
              borderColor: productApplicationsColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.products = !data.productGraphic?.data?.product || data.productGraphic.data.product.length === 0
          ? null
          : {
            labels: data.productGraphic.data.product.map((product) => `${product.product}: ${product.count} (${product.percentage}%)`),
            datasets: [{
              label: 'Porcentaje de Productos',
              data: data.productGraphic.data.product.map((product) => product.count),
              backgroundColor: productsColors.map(([rgbaColor]) => rgbaColor),
              borderColor: productsColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.productDoses = !data.productGraphic?.data?.dose || data.productGraphic.data.dose.length === 0
          ? null
          : {
            labels: data.productGraphic.data.dose.map((dose) => `${dose.dose || 'Sin especificar'}: ${dose.count} (${dose.percentage}%)`),
            datasets: [{
              label: 'Porcentaje de Dosis',
              data: data.productGraphic.data.dose.map((dose) => dose.count),
              backgroundColor: productDosesColors.map(([rgbaColor]) => rgbaColor),
              borderColor: productDosesColors.map(([, rgbColor]) => rgbColor),
              hoverOffset: 4
            }],
          };

        this.stationsKeys.forEach((stationKey) => {
          const stationData = data.stationGraphic?.data?.find((station) => station.StationType === stationKey);
          if (!stationData) return;

          const activityData = stationData.data.activity || [];
          const conditionData = stationData.data.condition || [];
          const plagueData = stationData.data.plague || [];

          const stationActivitiesColors = activityData.map(() => generateRandomColor());
          const stationConditionsColors = conditionData.map(() => generateRandomColor());
          const stationPlaguesColors = plagueData.map(() => generateRandomColor());

          this.stationActivities[stationKey] = !activityData || activityData.length === 0
            ? null
            : {
              labels: activityData.map((activity) => `${activity.activity}: ${activity.count} (${activity.percentage}%)`),
              datasets: [{
                label: 'Porcentaje de Actividad en ' + stationKey,
                data: activityData.map((activity) => activity.count),
                backgroundColor: stationActivitiesColors.map(([rgbaColor]) => rgbaColor),
                borderColor: stationActivitiesColors.map(([, rgbColor]) => rgbColor),
                hoverOffset: 4
              }],
            };

          this.stationConditions[stationKey] = !conditionData || conditionData.length === 0
            ? null
            : {
              labels: conditionData.map((condition) => `${condition.condition}: ${condition.count} (${condition.percentage}%)`),
              datasets: [{
                label: 'Porcentaje de Condiciones en ' + stationKey,
                data: conditionData.map((condition) => condition.count),
                backgroundColor: stationConditionsColors.map(([rgbaColor]) => rgbaColor),
                borderColor: stationConditionsColors.map(([, rgbColor]) => rgbColor),
                hoverOffset: 4
              }],
            };

          this.stationPlagues[stationKey] = !plagueData || plagueData.length === 0
            ? null
            : {
              labels: plagueData.map((plague) => `${plague.plague}: ${plague.count} (${plague.percentage}%)`),
              datasets: [{
                label: 'Plagas en ' + stationKey,
                data: plagueData.map((plague) => plague.count),
                backgroundColor: stationPlaguesColors.map(([rgbaColor]) => rgbaColor),
                borderColor: stationPlaguesColors.map(([, rgbColor]) => rgbColor),
                hoverOffset: 4
              }],
            };
        });
      },
      error: (error: any) => {
        console.error('Error al cargar los datos:', error);
      },
      complete: () => {
        this._loadingService.hide();
      }
    })
  }

  loadTrends(): void {
    this._loadingService.show();

    forkJoin({
      trendStationGraphic: this._reportsService.getTrendStationGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar trendStationGraphic:', error);
          return of({ data: {}, code: 0, message: 'Error' } as TrendStationResponse);
        })
      ),
      trendPlagueGraphic: this._reportsService.getTrendPlagueGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar trendPlagueGraphic:', error);
          return of({ data: [], code: 0, message: 'Error' } as TrendPlagueResponse);
        })
      ),
      trendProductGraphic: this._reportsService.getTrendProductGraphic(this.clientId, this.initialDate, this.finalDate).pipe(
        catchError((error) => {
          console.error('Error al cargar trendProductGraphic:', error);
          return of({ data: [], code: 0, message: 'Error' });
        })
      ),
    }).subscribe({
      next: (data: {
        trendStationGraphic: TrendStationResponse,
        trendPlagueGraphic: TrendPlagueResponse,
        trendProductGraphic: any,
      }) => {
        const trendPlagueLabels = Array.from(
          new Set(
            (data.trendPlagueGraphic?.data || []).flatMap((item) => {
              const key = Object.keys(item)[0];
              return item[key].map(trend => trend.date);
            })
          )
        );

        const trendProductLabels = Array.from(
          new Set(
            (data.trendProductGraphic?.data || []).flatMap((item: any) => {
              const key = Object.keys(item)[0];
              return item[key].map((trend: any) => trend.date);
            })
          )
        );

        this.trendPlague = {
          labels: trendPlagueLabels,
          datasets: (data.trendPlagueGraphic?.data || []).map((trend) => {
            const trendPlagueColors = generateRandomColor();

            const trendPlagueData = trend[Object.keys(trend)[0]].map((item: any) => item['max count']);
            const title = Object.keys(trend)[0];

            return {
              label: 'Tendencia de ' + title,
              data: trendPlagueData,
              fill: false,
              backgroundColor: trendPlagueColors[0],
              borderColor: trendPlagueColors[1],
              tension: 0.1
            };
          }),
        };

        this.trendProduct = {
          labels: trendProductLabels,
          datasets: (data.trendProductGraphic?.data || []).map((trend: any) => {
            const trendProductColors = generateRandomColor();

            const trendProductData = trend[Object.keys(trend)[0]].map((item: any) => item['max count']);
            const title = Object.keys(trend)[0];

            return {
              label: 'Tendencia de ' + title,
              data: trendProductData,
              fill: false,
              backgroundColor: trendProductColors[0],
              borderColor: trendProductColors[1],
              tension: 0.1
            };
          }),
        };

        const trendStationData = data.trendStationGraphic?.data || {};

        this.trendStation = Object.keys(trendStationData).reduce((acc: any, stationKey: string) => {
          const station = trendStationData[stationKey];

          if (!station) return acc;

          const stationLabels = Array.from(
            new Set(
              Object.values(station).flatMap((subCategory: any) =>
                subCategory.map((item: any) => item.event_date)
              )
            )
          );

          const stationDatasets = Object.keys(station).map((subCategoryKey) => {
            const subCategoryData = station[subCategoryKey];
            const stationColors = generateRandomColor();

            return {
              label: subCategoryKey,
              data: stationLabels.map(date => {
                const foundItem = subCategoryData.find((item: any) => item.event_date === date);
                return foundItem ? foundItem.count : 0;
              }),
              fill: false,
              backgroundColor: stationColors[0],
              borderColor: stationColors[1],
              tension: 0.1
            };
          });

          acc[stationKey] = {
            labels: stationLabels,
            datasets: stationDatasets
          };

          return acc;
        }, {});

        const trendStationKeys = Object.keys(this.trendStation);
        this.trendStationKeys = trendStationKeys;

        this.trendStationTitles = trendStationKeys.reduce((acc: any, stationKey: string) => {
          acc[stationKey] = 'Tendencia de ' + stationKey;
          return acc;
        }, {});
      },
      error: (error: any) => {
        console.error('Error al cargar las tendencias:', error);
      },
      complete: () => {
        this._loadingService.hide();
      }
    });
  }

  exportToPDF(): void {
    const element = this.elementRef.nativeElement;

    const currentDate = new Date();
    let date = {
      day: `${currentDate.getDate()}`.padStart(2, '0'),
      month: `${currentDate.getMonth() + 1}`.padStart(2, '0'),
      year: currentDate.getFullYear()
    };
    const formattedDate = `${date.day}-${date.month}-${date.year}`;

    const tempStyle = document.createElement('style');
    tempStyle.textContent = `
      * {
        color: inherit !important;
        background-color: inherit !important;
        border-color: inherit !important;
      }

      .bg-primary { background-color: #007ad9 !important; color: white !important; }
      .bg-success { background-color: #28a745 !important; color: white !important; }
      .bg-info { background-color: #17a2b8 !important; color: white !important; }
      .bg-warning { background-color: #ffc107 !important; color: black !important; }
      .text-primary { color: black !important; }
      .text-white { color: white !important; }
      .text-dark { color: black !important; }
    `;

    document.head.appendChild(tempStyle);

    const elementsToHide = element.querySelectorAll('.p-datatable-tbody a, .blocked-message, button, .hide-accordion');
    const originalDisplay: string[] = [];

    elementsToHide.forEach((el: HTMLElement) => {
      originalDisplay.push(el.style.display);
      el.style.display = 'none';
    });

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    }).then((canvas) => {
      elementsToHide.forEach((el: HTMLElement, index: number) => {
        el.style.display = originalDisplay[index];
      });

      document.head.removeChild(tempStyle);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = canvas.height * imgWidth / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Reporte_Cotizaciones_${formattedDate}.pdf`);
    }).catch((error) => {
      elementsToHide.forEach((el: HTMLElement, index: number) => {
        el.style.display = originalDisplay[index];
      });

      document.head.removeChild(tempStyle);
    });
  }
}
